import * as Schema from "effect/Schema";
import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { getLocalStorageItem, setLocalStorageItem } from "./useLocalStorage";

const WidthSchema = Schema.Finite;

export interface UseResizableWidthOptions {
  /** localStorage key the persisted width is stored under. */
  readonly storageKey: string;
  readonly defaultWidth: number;
  readonly minWidth: number;
  readonly maxWidth: number;
  /**
   * Which edge of the host element carries the drag handle:
   *   - "left"  → panel grows leftward (right-anchored panels)
   *   - "right" → panel grows rightward (left-anchored panels)
   */
  readonly edge: "left" | "right";
}

export interface ResizableWidthHandlers {
  readonly onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
}

export function resizableWidthFromPointer(input: {
  readonly edge: "left" | "right";
  readonly startX: number;
  readonly currentX: number;
  readonly startWidth: number;
}): number {
  const delta =
    input.edge === "left" ? input.startX - input.currentX : input.currentX - input.startX;
  return input.startWidth + delta;
}

/**
 * Width state for a side-anchored panel resized via a drag handle on the
 * specified edge. Width is read from localStorage on mount and persisted on
 * drag-end (not on every rAF tick — would otherwise be ~60 writes/sec).
 *
 * The hook updates an internal `width` state during drag (so the panel
 * follows the cursor live) and only commits to localStorage when the user
 * lifts the pointer.
 */
export function useResizableWidth(options: UseResizableWidthOptions): {
  readonly width: number;
  readonly handlers: ResizableWidthHandlers;
  readonly resizeTo: (width: number) => void;
} {
  const { storageKey, defaultWidth, minWidth, maxWidth, edge } = options;

  const clamp = useCallback(
    (value: number): number => {
      if (!Number.isFinite(value)) return defaultWidth;
      return Math.max(minWidth, Math.min(maxWidth, value));
    },
    [defaultWidth, maxWidth, minWidth],
  );

  // No cross-tab subscription: panel width is per-window state.
  const [width, setWidth] = useState<number>(() => {
    if (typeof window === "undefined") return defaultWidth;
    try {
      const stored = getLocalStorageItem(storageKey, WidthSchema);
      return clamp(stored ?? defaultWidth);
    } catch (error) {
      console.error("Could not read persisted panel width.", error);
      return defaultWidth;
    }
  });

  const clampedWidth = clamp(width);

  const dragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
    pending: number;
    rafId: number | null;
    target: HTMLElement;
  } | null>(null);

  const releasePointer = useCallback((pointerId: number) => {
    const state = dragStateRef.current;
    if (!state) return;
    if (state.rafId !== null) {
      cancelAnimationFrame(state.rafId);
    }
    try {
      if (state.target.hasPointerCapture(pointerId)) {
        state.target.releasePointerCapture(pointerId);
      }
    } catch {
      // pointer may already be released; harmless.
    }
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
    dragStateRef.current = null;
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      const target = event.currentTarget;
      try {
        target.setPointerCapture(event.pointerId);
      } catch {
        // Window-level listeners below keep the resize usable when pointer
        // capture is unavailable or released by the host shell.
      }
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      dragStateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startWidth: clampedWidth,
        pending: clampedWidth,
        rafId: null,
        target,
      };
    },
    [clampedWidth],
  );

  const movePointer = useCallback(
    (event: Pick<globalThis.PointerEvent, "clientX" | "pointerId" | "preventDefault">) => {
      const state = dragStateRef.current;
      if (!state || state.pointerId !== event.pointerId) return;
      event.preventDefault();
      state.pending = clamp(
        resizableWidthFromPointer({
          edge,
          startX: state.startX,
          currentX: event.clientX,
          startWidth: state.startWidth,
        }),
      );
      if (state.rafId !== null) return;
      state.rafId = requestAnimationFrame(() => {
        const active = dragStateRef.current;
        if (!active) return;
        active.rafId = null;
        setWidth(active.pending);
      });
    },
    [clamp, edge],
  );

  const finishPointer = useCallback(
    (pointerId: number) => {
      const state = dragStateRef.current;
      if (!state || state.pointerId !== pointerId) return;
      const finalWidth = clamp(state.pending);
      releasePointer(pointerId);
      // Commit once at drag-end to avoid 60Hz localStorage writes.
      try {
        setLocalStorageItem(storageKey, finalWidth, WidthSchema);
      } catch (error) {
        console.error("Could not persist panel width.", error);
      }
      setWidth(finalWidth);
    },
    [clamp, releasePointer, storageKey],
  );

  const cancelPointer = useCallback(
    (pointerId: number) => {
      const state = dragStateRef.current;
      if (!state || state.pointerId !== pointerId) return;
      // Don't persist a cancelled drag; revert to the start width.
      releasePointer(pointerId);
      setWidth(state.startWidth);
    },
    [releasePointer],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => movePointer(event),
    [movePointer],
  );
  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => finishPointer(event.pointerId),
    [finishPointer],
  );
  const onPointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => cancelPointer(event.pointerId),
    [cancelPointer],
  );

  useEffect(() => {
    const onWindowPointerMove = (event: globalThis.PointerEvent) => movePointer(event);
    const onWindowPointerUp = (event: globalThis.PointerEvent) => finishPointer(event.pointerId);
    const onWindowPointerCancel = (event: globalThis.PointerEvent) =>
      cancelPointer(event.pointerId);
    window.addEventListener("pointermove", onWindowPointerMove, { passive: false });
    window.addEventListener("pointerup", onWindowPointerUp);
    window.addEventListener("pointercancel", onWindowPointerCancel);
    return () => {
      window.removeEventListener("pointermove", onWindowPointerMove);
      window.removeEventListener("pointerup", onWindowPointerUp);
      window.removeEventListener("pointercancel", onWindowPointerCancel);
      const state = dragStateRef.current;
      if (state) releasePointer(state.pointerId);
    };
  }, [cancelPointer, finishPointer, movePointer, releasePointer]);

  const resizeTo = useCallback(
    (nextWidth: number) => {
      const resolvedWidth = clamp(nextWidth);
      try {
        setLocalStorageItem(storageKey, resolvedWidth, WidthSchema);
      } catch (error) {
        console.error("Could not persist panel width.", error);
      }
      setWidth(resolvedWidth);
    },
    [clamp, storageKey],
  );

  return {
    width: clampedWidth,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
    resizeTo,
  };
}
